import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    QueryList,
    Self,
    ViewChildren,
    ChangeDetectorRef,
} from '@angular/core';
import { NgControl, FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    BazisConfigurationService,
    SHARE_REPLAY_SETTINGS,
    VEHICLE_MASK_SETTINGS,
} from '@bazis/configuration.service';
import { combineLatest, Observable, shareReplay } from 'rxjs';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-vehicle-number',
    templateUrl: './input-vehicle-number.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputVehicleNumberComponent extends BazisControlValueAccessor {
    @Input() maskSettings: any = VEHICLE_MASK_SETTINGS;

    @Input() regionSuffix = 'rus';

    @ViewChildren('vehicleInput') vehicleInput: QueryList<ElementRef>;

    isDisabled = false;

    previousValue = [];

    parts = [];

    valueChanges$: Observable<any[]>;

    clrPartValues$;

    change$;

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        public configurationService: BazisConfigurationService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    extendOnInit() {
        this.initParts();
        this.initValueChanges();
        this.initChange();

        this.valueChanges$.subscribe();
    }

    protected initParts() {
        this.parts = this.configurationService.vehicleNumberMask.map((maskGroup) => {
            const maskSettings = { mask: maskGroup, ...this.maskSettings };
            return {
                control: new FormControl(''),
                maskSettings,
                placeholder: maskGroup.replaceAll(/\d|\D/g, '*'),
            };
        });
    }

    protected initValueChanges() {
        this.valueChanges$ = combineLatest([
            ...this.parts.map((v) =>
                v.control.valueChanges.pipe(
                    startWith(v.control.value),
                    shareReplay(SHARE_REPLAY_SETTINGS),
                ),
            ),
        ]).pipe(shareReplay(SHARE_REPLAY_SETTINGS));

        this.clrPartValues$ = this.valueChanges$.pipe(
            debounceTime(5),
            map((values) => values.map((part) => part.replace(/_/g, '').trim())),
            untilDestroyed(this),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    protected initChange() {
        this.change$ = this.valueChanges$.pipe(
            debounceTime(10),
            tap((values) => {
                const changedIndex = values.findIndex(
                    (v, index) =>
                        (v || this.previousValue[index]) && v !== this.previousValue[index],
                );
                if (changedIndex > -1) {
                    const inputs = this.vehicleInput.toArray();
                    if (
                        inputs[changedIndex].nativeElement.selectionStart ===
                            this.parts[changedIndex].maskSettings.mask.length &&
                        changedIndex < inputs.length - 1
                    ) {
                        inputs[changedIndex + 1].nativeElement.focus();
                        inputs[changedIndex + 1].nativeElement.setSelectionRange(0, 0);
                    }
                }

                this.previousValue = [...values];
                let isFull = true;
                values = values.map((part) => part.replace(/_/g, ''));
                values.forEach((value, valueIndex) => {
                    const notOptionalLength = this.configurationService.vehicleNumberMask[
                        valueIndex
                    ]
                        .split('')
                        .filter((symbol) => !this.maskSettings.patterns[symbol].optional).length;
                    if (notOptionalLength > value.length) {
                        isFull = false;
                    }
                });
                const value = values.join('');
                if (value === '' || isFull) {
                    this.onChange(value || null);
                } else {
                    this.ngControl.control.setErrors({ invalidVehicleNumber: true });
                }
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    onPressBackspace(i) {
        if (i === 0) return;

        const inputs = this.vehicleInput.toArray();
        if (
            inputs[i].nativeElement.selectionStart === 0 &&
            inputs[i].nativeElement.selectionStart === 0
        ) {
            inputs[i - 1].nativeElement.focus();
        }
    }

    onPressArrowLeft(event, i) {
        if (i === 0) return;

        const inputs = this.vehicleInput.toArray();
        if (
            inputs[i].nativeElement.selectionStart === 0 &&
            inputs[i].nativeElement.selectionEnd === 0
        ) {
            event.preventDefault();
            inputs[i - 1].nativeElement.focus();

            inputs[i - 1].nativeElement.setSelectionRange(
                this.parts[i - 1].maskSettings.mask.length,
                this.parts[i - 1].maskSettings.mask.length,
            );
        }
    }

    onPressArrowRight(event, i) {
        const inputs = this.vehicleInput.toArray();
        if (i === inputs.length - 1) return;
        if (
            inputs[i].nativeElement.selectionStart === inputs[i].nativeElement.selectionEnd &&
            inputs[i].nativeElement.selectionEnd === this.parts[i].maskSettings.mask.length
        ) {
            event.preventDefault();
            inputs[i + 1].nativeElement.focus();
            inputs[i + 1].nativeElement.setSelectionRange(0, 0);
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.parts.forEach((part) => {
            if (isDisabled) {
                part.control.disable();
            } else {
                part.control.enable();
            }
        });
    }

    public writeValue(value: any): void {
        if (!this.ngControl.control) return;
        let end = 0;

        value = value || '';
        this.configurationService.vehicleNumberMask.forEach((maskGroup, index) => {
            const start = end;
            const maxEnd = start + maskGroup.length;

            let currentValue = '';

            let maxValue = value.substring(start, maxEnd).toUpperCase().split('');
            maskGroup.split('').forEach((maskSymbol, indexMaskSymbol) => {
                if (maxValue.length < indexMaskSymbol) return;

                if (
                    this.maskSettings.patterns[maskSymbol].pattern.test(maxValue[indexMaskSymbol])
                ) {
                    currentValue += maxValue[indexMaskSymbol];
                }
            });

            end += currentValue.length;
            this.parts[index].control.setValue(currentValue.toUpperCase());
        });
    }
}
